import { Button, Input, Checkbox, Label, Separator } from '@design-system';
import { Plus, Trash2, ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { ConditionalBuilder } from '@/features/quote-and-apply-builder';
import { useOptionsEditor } from '../hooks/use-options-editor';

interface QuestionOption {
  id: string;
  value: string;
  wizardOnly?: boolean;
  tooltip?: string;
  conditional?: OptionConditionalBehavior;
}

interface OptionsEditorProps {
  options: QuestionOption[];
  allQuestions: Record<string, PolicyFormQuestion>;
  onChange: (options: QuestionOption[]) => void;
}

export function OptionsEditor({ options, allQuestions, onChange }: OptionsEditorProps) {
  const {
    expandedOption,
    conditionalOption,
    newOption,
    handleAddOption,
    handleUpdateOption,
    handleRemoveOption,
    handleMoveOption,
    toggleExpandedOption,
    openConditionalOption,
    closeConditionalOption,
    handleNewOptionIdChange,
    handleNewOptionValueChange,
  } = useOptionsEditor({ options, onChange });

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {options.map((option, index) => (
          <div
            key={`${option.id}-${index}`}
            className="rounded-lg border"
          >
            <div className="flex items-center gap-2 p-2">
              <div className="flex flex-col gap-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleMoveOption(index, 'up')}
                  disabled={index === 0}
                >
                  <ChevronUp className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleMoveOption(index, 'down')}
                  disabled={index === options.length - 1}
                >
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </div>

              <Input
                value={option.id}
                onChange={event => handleUpdateOption(index, { id: event.target.value })}
                placeholder="ID"
              />

              <Input
                value={option.value}
                onChange={event => handleUpdateOption(index, { value: event.target.value })}
                placeholder="Display value"
              />

              <Button
                variant="ghost"
                size="icon"
                onClick={() => toggleExpandedOption(option.id)}
              >
                <Settings className="h-4 w-4" />
              </Button>

              <Button
                variant="destructive"
                size="icon"
                onClick={() => handleRemoveOption(index)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {expandedOption === option.id && (
              <div className="space-y-3 border-t bg-gray-50 px-3 py-3">
                <div>
                  <Label>Tooltip</Label>
                  <Input
                    value={option.tooltip || ''}
                    onChange={event =>
                      handleUpdateOption(index, { tooltip: event.target.value || undefined })
                    }
                    placeholder="Tooltip text"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <Label className="flex cursor-pointer items-center gap-2">
                    <Checkbox
                      checked={option.wizardOnly || false}
                      onCheckedChange={checked =>
                        handleUpdateOption(index, { wizardOnly: !!checked })
                      }
                    />
                    <span className="text-sm text-gray-700">Wizard Only</span>
                  </Label>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openConditionalOption(option.id)}
                  >
                    <Settings className="mr-1 h-3 w-3" />
                    Conditional
                  </Button>
                </div>
              </div>
            )}

            {conditionalOption === option.id && (
              <ConditionalBuilder
                open={true}
                title={`Option "${option.value}" Conditional`}
                visibleCondition={option.conditional?.visible}
                questions={allQuestions}
                onChangeVisible={condition => {
                  handleUpdateOption(index, {
                    conditional: condition ? { visible: condition } : undefined,
                  });
                }}
                onClose={closeConditionalOption}
              />
            )}
          </div>
        ))}
      </div>

      <Separator />

      <div className="flex items-center gap-2">
        <Input
          value={newOption.id}
          onChange={handleNewOptionIdChange}
          placeholder="Option ID"
        />
        <Input
          value={newOption.value}
          onChange={handleNewOptionValueChange}
          placeholder="Display value"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddOption}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add
        </Button>
      </div>
    </div>
  );
}
